<template>
	<section v-editable="blok" :aria-label="blok.header">
		<div class="overflow-y-hidden" :class="blok.background ? 'bg-gradient-gray' : 'bg-background-secondary'">
			<div class="grid grid-cols-1 md:grid-cols-2 md:gap-12 lg:container xl:gap-20">
				<div
					class="max-lg:container col-span-1 pt-8 pb-6 md:pt-10 md:pb-12 flex flex-column items-center"
					:class="{'text-body-inverse': blok.background}"
				>
					<div class="max-w-full">
						<h2 class="typo-h1 md:mb-6">
							{{ blok.header }}
						</h2>
						<RichText :data="blok.text" dark-bg />
						<div v-if="blok.cta_button?.length" class="mt-4 md:mt-14">
							<StoryblokComponent
								v-for="item in blok.cta_button"
								:key="item._uid"
								:blok="item"
								class="btn btn-primary max-md:w-full max-md:justify-center md:btn-lg"
							/>
						</div>
					</div>
				</div>
				<div class="max-md:overflow-hidden isolate relative col-span-1">
					<div
						class="
						max-w-(--img-w) mx-auto mb-3 md:mt-8 md:mb-4
						rem:[--img-w:220px] md:rem:[--img-w:362px] lg:rem:[--img-w:462px]
						rem:[--gc-size:86px] md:rem:[--gc-size:130px] lg:rem:[--gc-size:195px]
						rem:[--oc-size:337px] md:rem:[--oc-size:500px] lg:rem:[--oc-size:764px]"
					>
						<div
							class="absolute w-(--oc-size) h-(--oc-size) left-1/2 top-1/4 translate-x-10 pointer-events-none mix-blend-multiply md:translate-x-20 lg:translate-x-28"
							:style="orangeCircleStyles"
						/>
						<div
							class="
							absolute w-(--gc-size) h-(--gc-size) left-1/2 translate-x-[calc(-50%-var(--img-w)/2)] pointer-events-none mix-blend-multiply
							lg:top-0 lg:-translate-y-1/2 lg:translate-x-[calc(-100%-30px)]"
							:style="greenCircleStyles"
						/>
						<div class="relative rounded-full overflow-hidden aspect-square mix-blend-multiply">
							<NuxtPicture
								v-if="blok.image?.filename"
								provider="storyblok"
								:modifiers="storyblokFocusModifiers(blok.image.focus)"
								:src="blok.image.filename"
								:alt="blok.image.alt || ''"
								sizes="xs:420px sm:600px md:600px lg:800px xl:800px xxl:800px"
								width="462"
								height="462"
								class="w-full h-full"
								:img-attrs="{class:'absolute w-full h-full object-center object-cover'}"
								loading="lazy"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
	import type {CtaBannerStoryblok} from '@/storyblok/types';

	interface Props {
		blok: CtaBannerStoryblok
	}

	defineProps<Props>();

	const img = useImage();

	const greenCircleStyles = computed(() => {
		const url = img('/images/ctaBanner/circle-green.svg', undefined, {sizes: 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw'});
		return {
			backgroundImage: `url('${url}')`,
			backgroundRepeat: 'no-repeat',
			backgroundSize: 'contain',
		};
	});

	const orangeCircleStyles = computed(() => {
		const url = img('/images/ctaBanner/circle-orange.svg', undefined, {sizes: 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw'});
		return {
			backgroundImage: `url('${url}')`,
			backgroundRepeat: 'no-repeat',
			backgroundSize: 'contain',
		};
	});
</script>
